import React, { useState, useEffect, useRef } from "react";
import styled from "@emotion/styled";
import { useNavigate, useLocation } from "react-router-dom";
import { Box, Button, TextField as MuiTextField, Alert as MuiAlert, Typography, Dialog, DialogContent, DialogTitle, IconButton } from "@mui/material";
import { spacing } from "@mui/system";
import { apiGatewayAxiosInstance, BUTTON_BACKGROUND_COLOR, BUTTON_TEXT_COLOR } from "../../utils/axios";
// import Setup2FA from "../../pages/auth/Setup2FA";
import useAuth from "../../hooks/useAuth";
import CloseIcon from "@mui/icons-material/Close";
import Setup2FA from "./Setup2FA";

const TextField = styled(MuiTextField)(spacing);
const Alert = styled(MuiAlert)(spacing);

function ValidateOTP() {
    const location = useLocation();
    const navigate = useNavigate();

    const [otp, setOtp] = useState("");
    const [isVeryfying, setIsVeryfying] = useState("");
    const [error, setError] = useState("");
    const verifyButtonRef = useRef(null);
    const [timer, setTimer] = useState(60);
    const [isResendEnabled, setIsResendEnabled] = useState(false);
    const payload = location.state;
    const { user } = useAuth();
    const [openMfa, setOpenMfa] = useState(false);

    useEffect(() => {
        let interval;
        if (timer > 0) {
            interval = setInterval(() => {
                setTimer(prev => prev - 1);
            }, 1000);
        } else {
            setIsResendEnabled(true);
        }
        // console.log(user);
        return () => clearInterval(interval);
    }, [timer]);

    useEffect(() => {
        if (!user?.email || !payload?.userId) {
            // navigate("/auth/login");
        }
    }, [user, payload, navigate]);

    const formatTime = seconds => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
    };

    const verifyOtp = async userData => {
        setIsVeryfying(true);
        const response = await apiGatewayAxiosInstance.post("/user_management/mfa/verify-mfa", userData);
        setIsVeryfying(false);
        return response;
    };

    const handleSubmit = async () => {
        try {
            if (otp.length !== 6) {
                throw new Error("Enter valid 6 digit otp");
            }
            const userData = {
                ...payload,
                mfaCode: otp,
            };

            const response = await verifyOtp(userData);
            if (response.status === 200) {
                sessionStorage.setItem("accessToken", response.data.token);
                sessionStorage.setItem("userRole", response.data.data.roleName);

                // apiGatewayAxiosInstance.defaults.headers.common["Authorization"] = `Bearer ${response.token}`;
                navigate("/dashboard");
            } else {
                throw new Error(response);
            }
        } catch (e) {
            const errorMessage = e.response?.data?.message || e.message || "Something went wrong. Please try again.";
            setIsVeryfying(false);
            setError(errorMessage);
        }
    };

    const handleResendOTP = async () => {
        try {
            await apiGatewayAxiosInstance.post("/user_management/mfa/initiate-mfa", payload);
            setIsResendEnabled(false);
            setTimer(60);
        } catch (e) {
            const errorMessage = e.response?.data?.message || e?.message || "Failed to resend OTP. Please try again.";
            setError(errorMessage);
            console.error("Error resending OTP:", e);
        }
    };

    const handleOpenMfa = async () => {
        setOpenMfa(true);
    };

    const handleClose = async () => {
        setOpenMfa(false);
    };

    useEffect(() => {
        if (!payload.userId) {
            navigate("/auth/login");
        }
    }, [payload?.userId]);

    return (
        <form
            onSubmit={e => {
                e.preventDefault();
                handleSubmit();
            }}
        >
            <Box maxWidth={400} mx="auto" mt={10} px={3} py={4} borderRadius={2} boxShadow={3} bgcolor="background.paper">
                {user?.mfaType && (<>
                    {user?.mfaType === "SMS_OTP" ? (
                        <Typography variant="body2" mb={2} textAlign="center" color="textSecondary">
                            We've sent an OTP to your registered mobile number. Please enter it below.
                        </Typography>
                    ) : (
                        <Typography variant="body2" mb={2} textAlign="center" color="textSecondary">
                            Please Enter The Code From Authenticator App.
                        </Typography>
                    )}

                    {error && (
                        <Alert severity="error" my={2}>
                            {error}
                        </Alert>
                    )}

                    <TextField
                        fullWidth
                        label={user?.mfaType === 'TOTP' ? 'Enter Code' : 'Enter OTP'}
                        variant="outlined"
                        value={otp}
                        onChange={e => {
                            setOtp(e.target.value);
                        }}
                        onKeyDown={e => {
                            if (e.key === "Enter") {
                                e.preventDefault(); // prevent form submit
                                if (verifyButtonRef.current) {
                                    verifyButtonRef.current.focus(); // shift focus to Verify button
                                }
                            }
                        }}
                        my={2}
                        inputProps={{ maxLength: 10 }}
                    />

                    <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        ref={verifyButtonRef}
                        onClick={handleSubmit}
                        sx={{
                            backgroundColor: BUTTON_BACKGROUND_COLOR,
                            color: BUTTON_TEXT_COLOR,
                            "&:hover": {
                                backgroundColor: BUTTON_BACKGROUND_COLOR,
                                boxShadow: "none",
                            },
                        }}
                        className="mt-3"
                        disabled={isVeryfying}
                    >
                        Verify
                    </Button>
                    {user?.mfaType === "SMS_OTP" && (<>
                        <Button fullWidth variant="outlined" sx={{ mt: 2 }} onClick={handleResendOTP} disabled={!isResendEnabled}>
                            {isResendEnabled ? "Resend OTP" : `Resend OTP in ${formatTime(timer)}`}
                        </Button>
                    </>)}
                </>)}
                {user?.mfaType !== "TOTP" && (
                    <>
                        <Setup2FA userEmail={user?.email} />
                        {/* <Button fullWidth variant="outlined" sx={{ mt: 2 }} onClick={handleOpenMfa}>
                            Setup TOTP method
                        </Button> */}
                    </>
                )}
            </Box>
            <Dialog open={openMfa} onClose={() => setOpenMfa(false)} maxWidth="sm" fullWidth>
                <DialogTitle
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                    }}
                >
                    <Typography variant="h5">Setup TOTP</Typography>
                    <IconButton onClick={() => handleClose()}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Setup2FA userEmail={user?.email} />
                </DialogContent>
            </Dialog>

        </form>
    );
}

export default ValidateOTP;
